import { toast, type Id, type ToastOptions } from "react-toastify";
import type { HttpError } from "@/lib/http/client";

type ActiveToast = {
  id: Id;
  type: ToastOptions["type"];
  message: string;
  generation: number;
};

let activeToast: ActiveToast | null = null;
let dismissalTimer: ReturnType<typeof setTimeout> | null = null;
let generation = 0;
const recentMessages = new Map<string, number>();

// Toasts are only transient feedback. Important decisions and unresolved errors
// must remain in the page, not in a notification that blocks the viewport.
const REPLAY_COOLDOWN_MS = 10_000;
const DISMISS_WATCHDOG_PADDING_MS = 250;
const MIN_AUTO_CLOSE_MS = 1_500;
const MAX_AUTO_CLOSE_MS = 6_000;

const defaultDuration = (type: ToastOptions["type"]): number => {
  if (type === "success") return 2400;
  if (type === "error") return 5000;
  if (type === "warning") return 4000;
  return 3000;
};

const finiteDuration = (
  type: ToastOptions["type"],
  requested: ToastOptions["autoClose"],
): number => {
  if (typeof requested !== "number" || !Number.isFinite(requested) || requested <= 0) {
    return defaultDuration(type);
  }
  return Math.max(MIN_AUTO_CLOSE_MS, Math.min(MAX_AUTO_CLOSE_MS, requested));
};

const normalizeToastMessage = (message: string): string =>
  message
    .toString()
    .trim()
    // Stav už komunikuje ikona toastu. Emoji v textu zbytečně duplikují význam.
    .replace(/\s*(?:🎉|❌|✅|⚠️|⚠|✨)+\s*$/gu, "")
    .trim();

const clearDismissalTimer = (): void => {
  if (dismissalTimer !== null) clearTimeout(dismissalTimer);
  dismissalTimer = null;
};

const onToastClosed = (
  id: Id,
  closedGeneration: number,
  callback: ToastOptions["onClose"],
): NonNullable<ToastOptions["onClose"]> => (...args) => {
  if (activeToast?.id === id && activeToast.generation === closedGeneration) {
    activeToast = null;
    clearDismissalTimer();
  }
  if (callback) (callback as (...values: unknown[]) => void)(...args);
};

// Do not depend on React-Toastify's animation or progress-bar event for removal.
// A pending/unmounted toast can report isActive=false, so that flag must not be
// used to decide whether to emit another toast into the hidden limit=1 queue.
const scheduleGuaranteedDismiss = (id: Id, currentGeneration: number, duration: number): void => {
  clearDismissalTimer();
  dismissalTimer = setTimeout(() => {
    if (activeToast?.id !== id || activeToast.generation !== currentGeneration) return;
    activeToast = null;
    dismissalTimer = null;
    toast.dismiss(id);
  }, duration + DISMISS_WATCHDOG_PADDING_MS);
};

const signatureFor = (message: string, type: ToastOptions["type"]): string =>
  JSON.stringify([type ?? "default", message]);

const wasRecentlyShown = (signature: string, now: number): boolean => {
  for (const [key, shownAt] of recentMessages) {
    if (now - shownAt >= REPLAY_COOLDOWN_MS) recentMessages.delete(key);
  }
  return recentMessages.has(signature);
};

/**
 * Exactly one transient notification, with a finite independent dismissal:
 * - Duplicate events cannot create a hidden toast queue or extend a timer.
 * - A new distinct result replaces the visible message in-place.
 * - Repeated polling/status messages cannot immediately reappear after closing.
 * - Even if the notification library misses its auto-close event, it is dismissed.
 */
export function showToastOnce(
  message: string,
  options?: ToastOptions,
): ReturnType<typeof toast> | undefined {
  const normalized = normalizeToastMessage(message);
  if (!normalized) return undefined;

  const type = options?.type;
  const signature = signatureFor(normalized, type);
  const now = Date.now();

  if (activeToast?.message === normalized && activeToast.type === type) {
    return activeToast.id;
  }
  if (wasRecentlyShown(signature, now)) return undefined;

  const duration = finiteDuration(type, options?.autoClose);
  const sharedOptions = {
    ...options,
    autoClose: duration,
    pauseOnFocusLoss: false,
    pauseOnHover: false,
  };
  const currentGeneration = ++generation;

  if (activeToast) {
    const id = activeToast.id;
    activeToast = { id, type, message: normalized, generation: currentGeneration };
    recentMessages.set(signature, now);
    toast.update(id, {
      ...sharedOptions,
      render: normalized,
      type: type ?? "default",
      onClose: onToastClosed(id, currentGeneration, options?.onClose),
    });
    scheduleGuaranteedDismiss(id, currentGeneration, duration);
    return id;
  }

  const id = toast(normalized, {
    ...sharedOptions,
    onClose: (...args) => {
      onToastClosed(id, currentGeneration, options?.onClose)?.(...args);
    },
  });
  activeToast = { id, type, message: normalized, generation: currentGeneration };
  recentMessages.set(signature, now);
  scheduleGuaranteedDismiss(id, currentGeneration, duration);
  return id;
}

type ResolvedToast = {
  showToast: boolean;
  type?: "error" | "info";
  message?: string;
};

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === "string" && value.trim().length > 0;

const extractErrorMeta = (
  err: HttpError,
): { status: number; code: string | null; message: string | null } => {
  const raw = err.data as
    | {
        code?: string;
        message?: string;
        meta?: { code?: string; message?: string };
      }
    | null
    | undefined;
  const meta = raw && typeof raw === "object" ? raw.meta : undefined;
  const code =
    raw && "code" in raw && isNonEmptyString(raw.code)
      ? raw.code
      : meta && isNonEmptyString(meta.code)
        ? meta.code
        : null;
  const backendMessage =
    raw && "message" in raw && isNonEmptyString(raw.message)
      ? raw.message.trim()
      : meta && isNonEmptyString(meta.message)
        ? meta.message.trim()
        : null;

  return {
    status: err.status,
    code,
    message: backendMessage,
  };
};

/**
 * Jediný zdroj pravdy pro rozhodnutí, zda a jak zobrazit toast na základě HttpError.
 * - Stavové kódy (ORG_PENDING, ORG_NOT_READY) nikdy nezobrazují toast.
 * - Vždy vrací uživatelsky čitelnou zprávu, nebo showToast=false.
 */
export function resolveToastFromHttpError(err: HttpError): ResolvedToast {
  const { status, code, message } = extractErrorMeta(err);

  // Stavové kódy – onboarding state machine, NE chyby.
  if (
    code === "ORG_PENDING" ||
    code === "ORG_NOT_READY" ||
    code === "NO_CURRENT_ACADEMIC_YEAR"
  ) {
    return { showToast: false };
  }

  let resolved: string | undefined;

  // Pokud backend poslal smysluplnou message, použij ji.
  if (isNonEmptyString(message)) {
    resolved = message.trim();
  } else {
    // Jinak rozhodni podle status kódu.
    if (status === 400 || status === 422) {
      resolved = "Zadaná data nejsou platná. Zkontroluj prosím formulář.";
    } else if (status === 403) {
      if (code && code.startsWith("ORG_")) {
        resolved = "Nemáš oprávnění k této akci v aktuální organizaci.";
      } else {
        resolved = "Nemáš oprávnění pro tuto akci.";
      }
    } else if (status === 404) {
      resolved = "Požadovaný záznam nebyl nalezen.";
    } else if (status === 409) {
      resolved = "Tato akce není v aktuálním stavu povolena.";
    } else if (status >= 500) {
      resolved = "Došlo k technické chybě na serveru. Zkus to prosím znovu.";
    } else {
      resolved = "Došlo k chybě. Zkus to prosím znovu.";
    }
  }

  if (!isNonEmptyString(resolved)) {
    return { showToast: false };
  }

  return {
    showToast: true,
    type: "error",
    message: resolved.trim(),
  };
}

/**
 * Pohodlný wrapper nad resolveToastFromHttpError – nikdy nezobrazí prázdný ani generický toast.
 */
export function showHttpErrorToastOnce(err: unknown): void {
  if (!err || typeof err !== "object") {
    showToastOnce("Došlo k technické chybě. Zkus to prosím znovu.", {
      type: "error",
    });
    return;
  }

  const maybeHttpError = err as HttpError;
  if (typeof maybeHttpError.status !== "number") {
    showToastOnce("Došlo k technické chybě. Zkus to prosím znovu.", {
      type: "error",
    });
    return;
  }

  const resolved = resolveToastFromHttpError(maybeHttpError);
  if (!resolved.showToast || !resolved.message) return;
  showToastOnce(resolved.message, { type: resolved.type ?? "error" });
}
